// @ts-check
// Aba Notas (doc 10, 6.3): cartões em grade com título, prévia do texto e data; "Nova nota" e o clique num cartão
// abrem a nota no painel lateral, com texto formatado (contrato 08, seção 5). A nota fica vinculada via noteTarget.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { createLiveRecordList } from '../../data/record-store.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { formatRelativeToNow } from '../../lib/format.js';
import { markdownToPlainText } from '../../lib/rich-text.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Button } from '../../ui/button.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { openNoteEditorPanel } from '../side-panel/side-panel-state.js';
import { openRecord } from '../side-panel/open-record.js';
import { findActivitiesForRecord, getActivityBodyField, getBodyText } from './activity-targets.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ objectNameSingular: string, recordId: string }} props
 */
export function NotesTab({ objectNameSingular, recordId }) {
  const noteObject = objectsByNameSingular.value.get('note');
  const [liveList] = useState(() => createLiveRecordList('note'));
  useEffect(() => () => liveList.dispose(), []);
  const [isLoading, setIsLoading] = useState(true);
  const notes = liveList.records.value;

  useEffect(() => {
    setIsLoading(true);
    findActivitiesForRecord('note', objectNameSingular, recordId)
      .then(({ activities }) => {
        liveList.records.value = activities;
      })
      .catch(showErrorSnackbar)
      .finally(() => setIsLoading(false));
  }, [objectNameSingular, recordId]);

  if (!noteObject) return html`<${EmptyState} icon="IconNotes" title=${t('notes.unavailable')} />`;
  const bodyField = getActivityBodyField(noteObject);

  const openNote = (/** @type {string | null} */ noteId) => openNoteEditorPanel({
    noteId,
    targetObjectNameSingular: objectNameSingular,
    targetRecordId: recordId,
    onSaved: (note, change) => {
      const others = liveList.records.value.filter((candidate) => candidate.id !== note.id);
      liveList.records.value = change === 'deleted' ? others : liveList.records.value.some((candidate) => candidate.id === note.id)
        ? liveList.records.value.map((candidate) => (candidate.id === note.id ? { ...candidate, ...note } : candidate))
        : [note, ...others];
    },
  });

  return html`<div class="activity-tab">
    <div class="activity-tab-header">
      <span class="activity-tab-title">${t('notes.title')}<span class="activity-tab-count">${notes.length}</span></span>
      <${Button} icon="IconPlus" title=${t('notes.new')} size="small" variant="secondary" onClick=${() => openNote(null)} />
    </div>
    ${isLoading
      ? html`<div class="note-grid">${[0, 1].map((index) => html`<div key=${index} class="note-card"><${Skeleton} width="60%" height=${14} /><${Skeleton} width="90%" height=${12} /></div>`)}</div>`
      : notes.length === 0
        ? html`<${EmptyState}
            icon="IconNotes"
            title=${t('notes.emptyTitle')}
            subtitle=${t('notes.emptySubtitle')}
            action=${html`<${Button} variant="secondary" icon="IconPlus" title=${t('notes.new')} onClick=${() => openNote(null)} />`}
          />`
        : html`<div class="note-grid">
            ${notes.map(
              (note) => html`<button type="button" class="note-card" key=${note.id} onClick=${(/** @type {MouseEvent} */ event) => {
                // nota existente abre como registro no painel (corpo, relações, timeline); dentro do painel, empilha
                openRecord({ objectNameSingular: 'note', recordId: note.id, openIn: 'SIDE_PANEL', source: /** @type {Element} */ (event.currentTarget) });
              }}>
                <span class="note-card-title">${note.title || t('notes.untitled')}</span>
                <span class="note-card-body">${bodyField ? markdownToPlainText(getBodyText(note[bodyField.name])) : ''}</span>
                <span class="note-card-footer">${formatRelativeToNow(note.createdAt)}</span>
              </button>`,
            )}
          </div>`}
  </div>`;
}
